import { AxiosResponse, CancelToken } from "axios";
import moment from "moment";

import { ImageData } from "../components/ImagePreviewDialog";
import { AppUsageEvolutionDTO } from "../models/dto/AppUsageEvolutionDTO";
import { AssigneeUseTermDTO } from "../models/dto/AssigneeUseTermDTO";
import { BranchDTO } from "../models/dto/BranchDTO";
import { DeviceDTO } from "../models/dto/DeviceDTO";
import { EmployeeDTO } from "../models/dto/EmployeeDTO";
import { LogDistanceDTO } from "../models/dto/LogDistanceDTO";
import { NoticeDTO } from "../models/dto/NotificationDTO";
import { ObservationDTO } from "../models/dto/ObservationDTO";
import OccurrencyDTO from "../models/dto/OccurrencyDTO";
import { PeriodDTO } from "../models/dto/PeriodDTO";
import { PreRegisterDTO } from "../models/dto/PreRegisterDTO";
import { PunchCardDetailsChartDTO } from "../models/dto/PunchCardDetailsDTO";
import { PunchCardMirrorStatusDTO } from "../models/dto/PunchCardMirrorStatusDTO";
import { RegisterRankingDTO } from "../models/dto/RegisterRankingDTO";
import { TermsDTO } from "../models/dto/TermsDTO";
import { TermToSignDTO } from "../models/dto/TermToSignDTO";
import { AppEnum } from "../models/enum/AppEnum";
import { Page } from "../models/Page";
import { isEmpty } from "../utils/validation.util";

import { api } from "./api";

export interface SendNoticeRequestData {
  reference: number;
  title: string;
  message: string;
  identifier?: number;
  type: string;
}

export interface ObservationsGetRequestData {
  employeeCode?: number;
  reference?: number;
  period?: PeriodDTO;
}

export interface ObservationsPostRequestData {
  dataPeriodoApuracao: string;
  gratificacao: string;
  numeroCadastro: number;
  observacao: string;
  valorBonificacao: number;
  quantidadeMinutos: number;
}

export interface PunchCardMirrorStatusGetRequestData {
  reference?: number;
  period?: PeriodDTO;
}

export const findAllEmployeeFromFaceRecognition = async (
  reference?: number,
): Promise<Page<EmployeeDTO>> => {
  return api.get("/gdp/relatorios/funcionarios/", {
    params: {
      ...(reference ? { referencia: reference } : {}),
    },
  });
};

export const findOccurrences = async (
  initialDate: string,
  finalDate: string,
  reference: number,
  cancelToken?: CancelToken,
): Promise<Page<OccurrencyDTO>> => {
  return api.get(`/gdp/relatorios/ocorrencias/${reference}`, {
    params: {
      filtroInicio: initialDate,
      filtroFim: finalDate,
    },
    cancelToken,
  });
};

export const findAllSignedTerms = async (
  reference: number,
  cancelToken?: CancelToken,
): Promise<Page<TermsDTO>> => {
  return api.get("/gdp/termos/assinatura", {
    params: {
      ...(reference ? { referencia: reference } : {}),
    },
    cancelToken,
  });
};

export const findAllSignedPunchCardsByPeriod = async (
  data: PunchCardMirrorStatusGetRequestData,
): Promise<Page<PunchCardMirrorStatusDTO>> => {
  return api.get("/gdp/assinaturas", {
    params: {
      ...(data.reference && { referencia: data.reference }),
      ...(data.period && {
        dataPeriodo: moment(data.period.mesFechamento, "DD/MM/YYYY").format(
          "YYYY-MM-DD",
        ),
      }),
      tipoAssinatura: "ESPELHO",
    },
  });
};

export const findAllAppPreRegistrations = (
  reference: number,
  status?: string,
  cancelToken?: CancelToken,
): Promise<Page<PreRegisterDTO>> => {
  return api.get("/gdp/funcionarios/pre-cadastro", {
    cancelToken,
    params: {
      referencia: reference,
      ...(!isEmpty(status) ? { status } : {}),
    },
  });
};

export const deleteEmployeeFaceRecognition = async (
  registrationNumber: number,
  numeroCracha: number,
): Promise<void> => {
  await api.delete(`gdp/funcionarios/v3/${registrationNumber}/${numeroCracha}`);
};

export const approveOrReproveEmployeeRegister = async (
  hashArquivo: string,
  status: string,
  reason?: string,
): Promise<void> => {
  await api.put("gdp/funcionarios/pre-cadastro", {
    hashArquivo,
    status,
    ...(reason && { motivo: reason }),
  });
};

export const findTermsOfAgreementSign = async (
  reference: number,
): Promise<any> => {
  await api.get(`gdp/funcionarios/assinaturas/referencia/${reference}`);
};

export const findNoticesyEmployeeAndPeriod = async (
  employee: EmployeeDTO,
  period: PeriodDTO,
): Promise<Page<NoticeDTO>> => {
  return api.get<NoticeDTO[]>("/notifications/info", {
    params: {
      identifier: employee.numeroCadastro,
      type: AppEnum.APP_PONTO.description,
      dataPeriodo: moment(period.mesFechamento, "DD/MM/YYYY").format(
        "YYYY-MM-DD",
      ),
    },
  });
};

export const sendNotices = async (
  data: SendNoticeRequestData[],
): Promise<void> => {
  await api.post("/notifications/info/create", data);
};

export const findObservations = async (
  data: ObservationsGetRequestData,
): Promise<AxiosResponse<ObservationDTO[]>> => {
  return api.get<ObservationDTO[]>("/gdp/pontos/observacoes", {
    params: {
      ...(data.employeeCode && { numeroCadastro: data.employeeCode }),
      ...(data.reference && { referencia: data.reference }),
      ...(data.period && {
        dataPeriodoApuracao: moment(
          data.period.mesFechamento,
          "DD/MM/YYYY",
        ).format("YYYY-MM-DD"),
      }),
    },
  });
};

export const saveObservation = async (
  observation: ObservationsPostRequestData,
): Promise<void> => {
  await api.post("/gdp/pontos/observacoes", {
    dataPeriodoApuracao: observation.dataPeriodoApuracao,
    observacao: observation.observacao,
    numeroCadastro: observation.numeroCadastro,
    valorBonificacao: observation.valorBonificacao,
    gratificacao: observation.gratificacao,
    quantidadeMinutos: observation.quantidadeMinutos,
  });
};

export const createTabletRegisterCode = async (
  reference: number,
): Promise<AxiosResponse<DeviceDTO>> => {
  return api.post("/gdp/dispositivos", {
    referencia: reference,
  });
};

export const createCondominium = async (
  condominiumName: string,
  condominiumLatitude: string,
  condominiumLongitude: string,
): Promise<void> => {
  await api.post("gdp/coordenadas-referencia/", {
    referencia: condominiumName,
    latitude: condominiumLatitude,
    longitude: condominiumLongitude,
  });
};

export const getPreRegisterImageData = async (
  fileHash: string,
): Promise<ImageData> => {
  const { data, headers } = await api.get(
    `gdp/funcionarios/pre-cadastro/${fileHash}/download`,
    {
      responseType: "arraybuffer",
      headers: {
        "Content-Type": "image/png;image/jpeg;image/jpg",
      },
    },
  );

  const contentType = headers["content-type"].split("/")[1];

  return {
    fileBuffer: data,
    contentType,
  };
};

export const findAllFailedLoginAttempts = (
  reference: number,
): Promise<Page<LogDistanceDTO>> => {
  return api.get("/gdp/pontos/log-distancia", {
    params: {
      flagSucesso: false,
      referencia: reference,
    },
  });
};

export const checkTermToSign = async (
  reference: number,
): Promise<AxiosResponse<TermToSignDTO>> => {
  return api.get(`gdp/termos/assinatura/condominio/${reference}`);
};

export const findAssigneeUseTerm = async (): Promise<
  AxiosResponse<AssigneeUseTermDTO>
> => {
  return api.get("gdp/termos/sindico");
};

export const confirmUseTermReading = async (
  termsOfUseID: string,
  userID: string,
) => {
  await api.post("/gdp/termos/assinatura", {
    idTermoUso: termsOfUseID,
    idUsuario: userID,
    dataHoraAssinatura: moment().format("YYYY-MM-DDTHH:mm"),
  });
};

export const findAppUsageEvolution = async (
  type: string,
  year: string,
  cancelToken?: CancelToken,
): Promise<AxiosResponse<AppUsageEvolutionDTO[]>> => {
  return api.get(`gdp/relatorios/evolucao-uso-${type}/`, {
    params: {
      ano: year,
    },
    cancelToken,
  });
};

export const getPunchCardDetailsChartData = async (
  initialDate: string,
  finalDate: string,
  reference: number,
  cancelToken?: CancelToken,
): Promise<AxiosResponse<PunchCardDetailsChartDTO[]>> => {
  return api.get(`gdp/relatorios/horas-trabalhadas/${reference}`, {
    params: {
      periodoDe: initialDate,
      periodoAte: finalDate,
    },
    cancelToken,
  });
};

export const getPunchCardPeriodChartData = async (
  initialDate: string,
  finalDate: string,
  reference: number,
  cancelToken?: CancelToken,
): Promise<AxiosResponse<PunchCardDetailsChartDTO[]>> => {
  return api.get(`gdp/relatorios/horas-trabalhadas-por-periodo/${reference}`, {
    params: {
      periodoDe: initialDate,
      periodoAte: finalDate,
    },
    cancelToken,
  });
};

export const findAbsences = async (): Promise<
  AxiosResponse<OccurrencyDTO[]>
> => {
  return api.get("gdp/relatorios/marcacao-afastamento");
};

interface AppUsageEvolutionParams {
  type: string;
  startDate: string;
  endDate: string;
  branchId: number;
}

export const downloadAppUsageEvolution = async (
  params: AppUsageEvolutionParams,
) => {
  const { type, startDate, endDate, branchId } = params;
  const response = api
    .get(`/gdp/csvs/evolucao-uso-${type}`, {
      params: {
        idRegional: branchId,
        periodoDe: startDate,
        periodoAte: endDate,
      },
    })
    .catch(error => {
      if (error?.response?.status === 400 || error?.response?.status === 204) {
        return undefined;
      }
      return Promise.reject(error);
    });
  return response;
};

export const findAllBranches = async (
  cancelToken?: CancelToken,
): Promise<AxiosResponse<BranchDTO[]>> => {
  return api.get("gdp/regionais", { cancelToken });
};

export const findExtraHourRanking = async (
  reference: number,
  startDate: string,
  endDate: string,
  cancelToken?: CancelToken,
): Promise<AxiosResponse<Array<RegisterRankingDTO[]>>> => {
  return api.get(`/gdp/relatorios/ranking-horas-extras/${reference}`, {
    params: {
      periodoDe: startDate,
      periodoAte: endDate,
    },
    cancelToken,
  });
};

export const findEmployeesDelayRanking = async (
  reference: number,
  startDate: string,
  endDate: string,
  cancelToken?: CancelToken,
): Promise<AxiosResponse<Array<RegisterRankingDTO[]>>> => {
  return api.get(`/gdp/relatorios/ranking-horas-atrasos/${reference}`, {
    params: {
      periodoDe: startDate,
      periodoAte: endDate,
    },
    cancelToken,
  });
};
